import { Injectable } from '@angular/core';
import { format, startOfMonth, endOfMonth, addMonths, startOfDay } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';

/**
 * Centralized timezone handling service to prevent timezone conversion chaos.
 * 
 * RULES:
 * 1. All internal dates are stored as native Date objects in UTC
 * 2. All API calls expect UTC timestamps
 * 3. All user-facing displays should show times in the site's timezone
 * 4. All date boundaries (start/end of month) should be calculated in site timezone
 */
@Injectable({
  providedIn: 'root',
})
export class TimezoneService {

  /**
   * Get the current date/time in a specific timezone
   */
  now(timezone: string): Date {
    return utcToZonedTime(new Date(), timezone);
  }

  /**
   * Get start of month for a given date in site timezone
   */
  startOfMonthInTimezone(date: Date, timezone: string): Date {
    const dateInTz = utcToZonedTime(date, timezone);
    return startOfMonth(dateInTz);
  }

  /**
   * Get end of month for a given date in site timezone
   */
  endOfMonthInTimezone(date: Date, timezone: string): Date {
    const dateInTz = utcToZonedTime(date, timezone);
    return endOfMonth(dateInTz);
  }

  /**
   * Convert a timezone-aware date back to UTC for API calls
   */
  toApiDate(date: Date, timezone: string): Date {
    return zonedTimeToUtc(date, timezone);
  }

  /**
   * Convert UTC date from API to timezone-aware date for display
   */
  fromApiDate(utcDate: Date | string, timezone: string): Date {
    const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    return utcToZonedTime(date, timezone);
  }

  /**
   * Format a date for API (as date string in site timezone)
   */
  formatForApi(date: Date, timezone: string): string {
    return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
  }

  /**
   * Format a time for display in a specific timezone
   */
  formatTimeInTimezone(date: Date, timezone: string, formatStr: string = 'h:mm a'): string {
    return formatInTimeZone(date, timezone, formatStr);
  }

  /**
   * Format a date/time according to site timezone (replaces date-utils.ts functionality)
   * @param date The date to format (can be string or Date)
   * @param timezone The timezone to use (defaults to UTC if not provided)
   * @param format Optional format string (defaults to 'yyyy-MM-dd HH:mm:ss')
   * @returns A formatted date string in the specified timezone
   */
  formatDateInTimeZone(
    date: Date | string,
    timezone: string,
    formatStr: string = 'yyyy-MM-dd HH:mm:ss',
  ): string {
    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    return formatInTimeZone(parsedDate, timezone, formatStr);
  }

  /**
   * Format a date for recurrence dtstart (must be consistent)
   * The input date should already be in the correct timezone context
   */
  formatForRecurrence(date: Date): string {
    // Always format as start of day without timezone conversion
    // NOTE: The input date should already represent the correct calendar day
    return format(startOfDay(date), 'yyyy-MM-dd');
  }

  /**
   * Generate month options array for date selectors
   * Returns dates that represent the 1st of each month in the site timezone
   */
  generateMonthOptions(timezone: string, rangeMonths: number = 12): Date[] {
    const now = this.now(timezone);
    const currentMonthStart = startOfMonth(now);
    const options: Date[] = [];

    for (let n = -rangeMonths; n < rangeMonths + 1; n++) {
      // Keep in timezone context - don't convert to UTC
      options.push(addMonths(currentMonthStart, n));
    }

    return options;
  }

  /**
   * Get month boundaries for API queries
   * Returns { start: UTC Date, end: UTC Date } for API calls
   */
  getMonthBoundariesForApi(monthDate: Date, timezone: string): { startDateUTC: Date, endDateUTC: Date } {
    const startOfMonthInTz = this.startOfMonthInTimezone(monthDate, timezone);
    const endOfMonthInTz = this.endOfMonthInTimezone(monthDate, timezone);
    
    return {
      startDateUTC: this.toApiDate(startOfMonthInTz, timezone),
      endDateUTC: this.toApiDate(endOfMonthInTz, timezone)
    };
  }

}